/**
 * Device Tracking Service for Guest User Management
 * Handles device fingerprinting, customer linking, and phone usage tracking
 * ONLY for non-logged-in users - does not affect authenticated user flows
 */

import { supabase } from '../config/supabase'
import { getDeviceId, validatePhoneNumber } from '../utils/deviceFingerprint'

/**
 * Initialize device session when user lands on menu page
 * @param {string} restaurantId - Restaurant UUID
 * @param {string} tableId - Table UUID
 * @returns {Promise<Object>} - { sessionId, deviceId, returningCustomer, isReturning }
 */
export const initializeDeviceSession = async (restaurantId, tableId) => {
  try {
    console.log('🔄 Initializing device session...', { restaurantId, tableId })
    
    // Get device fingerprint
    const deviceId = getDeviceId()
    
    if (!deviceId) {
      console.warn('⚠️ No device ID found - device tracking not initialized')
      return { sessionId: null, deviceId: null, returningCustomer: null, isReturning: false }
    }
    
    console.log('🔐 Device ID:', deviceId)
    
    // Get client IP address (best effort)
    let ipAddress = null
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json')
      const ipData = await ipResponse.json()
      ipAddress = ipData.ip
    } catch (error) {
      console.warn('⚠️ Could not fetch IP address:', error)
    }
    
    // Call database function to get or create device record
    const { data: deviceData, error: deviceError } = await supabase
      .rpc('get_or_create_device', {
        p_device_fingerprint: deviceId,
        p_device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          timestamp: new Date().toISOString()
        }
      })
    
    if (deviceError) {
      console.error('❌ Error calling get_or_create_device:', deviceError)
      throw deviceError
    }
    
    console.log('✅ Device record:', deviceData)
    
    const deviceRecord = deviceData[0] || {}
    const { device_id, linked_customer_id, total_orders, is_returning } = deviceRecord
    
    // Get linked customer details if exists
    let returningCustomer = null
    if (linked_customer_id) {
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('id, full_name, email, phone, total_orders, loyalty_tier')
        .eq('id', linked_customer_id)
        .single()
      
      if (!customerError && customerData) {
        returningCustomer = {
          id: customerData.id,
          name: customerData.full_name,
          email: customerData.email,
          phone: customerData.phone,
          totalOrders: customerData.total_orders || 0,
          loyaltyTier: customerData.loyalty_tier
        }
        console.log('🎉 Returning customer detected:', returningCustomer)
      }
    }
    
    // Generate unique session ID
    const generatedSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    // Create customer session
    const { data: sessionData, error: sessionError } = await supabase
      .from('customer_sessions')
      .insert({
        session_id: generatedSessionId,
        restaurant_id: restaurantId,
        table_id: tableId,
        device_fingerprint: deviceId,
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        },
        ip_address: ipAddress,
        session_status: 'active',
        last_activity_at: new Date().toISOString()
      })
      .select()
      .single()
    
    if (sessionError) {
      console.error('❌ Error creating customer session:', sessionError)
      throw sessionError
    }
    
    console.log('✅ Customer session created:', sessionData)
    
    // Store session ID in sessionStorage
    sessionStorage.setItem('ordyrr_session_id', sessionData.id)
    
    return {
      sessionId: sessionData.id,
      deviceId,
      returningCustomer,
      isReturning: is_returning || false,
      totalOrders: total_orders || 0
    }
  } catch (error) {
    console.error('❌ Error initializing device session:', error)
    throw error
  }
}

/**
 * Find or create customer record
 * @param {string} name - Customer name
 * @param {string} phone - Customer phone
 * @param {string} email - Customer email (optional)
 * @param {string} deviceId - Device fingerprint
 * @returns {Promise<Object>} - Customer object
 */
export const findOrCreateCustomer = async (name, phone, email, deviceId) => {
  try {
    console.log('🔍 Finding or creating customer...', { name, phone, email, deviceId })
    
    // First, try to find existing customer by phone
    let customer = null
    
    if (phone) {
      const { data: existingCustomer, error: searchError } = await supabase
        .from('customers')
        .select('*')
        .eq('phone', phone)
        .maybeSingle()
      
      if (!searchError && existingCustomer) {
        customer = existingCustomer
        console.log('✅ Found existing customer by phone:', customer.id)
        
        // Update customer details
        const updateData = {
          full_name: name || customer.full_name,
          updated_at: new Date().toISOString()
        }
        
        // Update email if provided and different
        if (email && email !== customer.email) {
          updateData.email = email
        }
        
        // Link device to customer if not already linked
        if (customer.device_fingerprint !== deviceId) {
          updateData.device_fingerprint = deviceId
          updateData.last_device_info = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            timestamp: new Date().toISOString()
          }
        }
        
        const { data: updatedCustomer, error: updateError } = await supabase
          .from('customers')
          .update(updateData)
          .eq('id', customer.id)
          .select()
          .single()
        
        if (!updateError) {
          customer = updatedCustomer
          console.log('✅ Customer updated:', customer.id)
        }
      }
    }
    
    // If no customer found, create new one
    if (!customer) {
      const { data: newCustomer, error: createError } = await supabase
        .from('customers')
        .insert({
          full_name: name || 'Guest Customer',
          phone: phone || null,
          email: email || null,
          is_guest: true,
          device_fingerprint: deviceId,
          last_device_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            timestamp: new Date().toISOString()
          },
          total_orders: 0,
          loyalty_tier: 'bronze'
        })
        .select()
        .single()
      
      if (createError) {
        console.error('❌ Error creating customer:', createError)
        throw createError
      }
      
      customer = newCustomer
      console.log('✅ New customer created:', customer.id)
    }
    
    // Update device_tracking to link customer
    if (deviceId) {
      const { error: deviceUpdateError } = await supabase
        .from('device_tracking')
        .update({
          linked_customer_id: customer.id,
          updated_at: new Date().toISOString()
        })
        .eq('device_fingerprint', deviceId)
      
      if (deviceUpdateError) {
        console.warn('⚠️ Could not link device to customer:', deviceUpdateError)
      } else {
        console.log('✅ Device linked to customer')
      }
    }
    
    return customer
  } catch (error) {
    console.error('❌ Error in findOrCreateCustomer:', error)
    throw error
  }
}

/**
 * Track phone number usage for fraud detection
 * @param {string} phone - Phone number
 * @param {string} deviceId - Device fingerprint
 * @param {string} customerId - Customer UUID
 * @returns {Promise<Object>} - { isSuspicious, reason }
 */
export const trackPhoneUsage = async (phone, deviceId, customerId) => {
  try {
    console.log('📞 Tracking phone usage...', { phone, deviceId, customerId })
    
    if (!phone || !deviceId) {
      return { isSuspicious: false, reason: null }
    }
    
    // Insert or update phone usage record
    const { data: existingUsage, error: searchError } = await supabase
      .from('phone_usage_tracking')
      .select('*')
      .eq('phone_number', phone)
      .eq('device_fingerprint', deviceId)
      .maybeSingle()
    
    if (existingUsage) {
      // Update existing record
      const { error: updateError } = await supabase
        .from('phone_usage_tracking')
        .update({
          order_count: existingUsage.order_count + 1,
          last_used_at: new Date().toISOString(),
          customer_id: customerId
        })
        .eq('id', existingUsage.id)
      
      if (updateError) {
        console.warn('⚠️ Could not update phone usage:', updateError)
      }
    } else {
      // Create new record
      const { error: insertError } = await supabase
        .from('phone_usage_tracking')
        .insert({
          phone_number: phone,
          device_fingerprint: deviceId,
          customer_id: customerId,
          order_count: 1,
          first_used_at: new Date().toISOString(),
          last_used_at: new Date().toISOString(),
          is_verified: false
        })
      
      if (insertError) {
        console.warn('⚠️ Could not insert phone usage:', insertError)
      }
    }
    
    // Check if device has used too many different phone numbers
    const { data: phoneCount, error: countError } = await supabase
      .from('phone_usage_tracking')
      .select('phone_number', { count: 'exact' })
      .eq('device_fingerprint', deviceId)
    
    if (!countError && phoneCount && phoneCount.length > 5) {
      console.warn('⚠️ Device has used more than 5 different phone numbers')
      
      // Flag device as suspicious
      const { error: flagError } = await supabase
        .from('device_tracking')
        .update({
          is_suspicious: true,
          suspicious_reason: `Device has used ${phoneCount.length} different phone numbers`,
          updated_at: new Date().toISOString()
        })
        .eq('device_fingerprint', deviceId)
      
      if (flagError) {
        console.warn('⚠️ Could not flag device:', flagError)
      }
      
      return {
        isSuspicious: true,
        reason: `This device has been used with ${phoneCount.length} different phone numbers`
      }
    }
    
    console.log('✅ Phone usage tracked')
    return { isSuspicious: false, reason: null }
  } catch (error) {
    console.error('❌ Error tracking phone usage:', error)
    // Don't throw - this is not critical
    return { isSuspicious: false, reason: null }
  }
}

/**
 * Get applicable offers for a customer
 * @param {Object} customer - Customer object
 * @returns {Promise<Array>} - Array of applicable offers
 */
export const getApplicableOffers = async (customer) => {
  try {
    console.log('🎁 Getting applicable offers for customer...', customer?.id)
    
    if (!customer) {
      return []
    }
    
    const now = new Date().toISOString()
    
    // Get all active offers
    const { data: offers, error: offersError } = await supabase
      .from('offers')
      .select('*')
      .eq('is_active', true)
      .lte('valid_from', now)
      .gte('valid_until', now)
    
    if (offersError) {
      console.error('❌ Error fetching offers:', offersError)
      return []
    }
    
    if (!offers || offers.length === 0) {
      console.log('ℹ️ No active offers found')
      return []
    }
    
    // Filter offers based on customer eligibility
    const applicableOffers = []
    
    for (const offer of offers) {
      // Check if customer has already used this offer
      const { data: usage, error: usageError } = await supabase
        .from('customer_offers')
        .select('id', { count: 'exact' })
        .eq('customer_id', customer.id)
        .eq('offer_id', offer.id)
      
      if (usageError) {
        console.warn('⚠️ Could not check offer usage:', usageError)
        continue
      }
      
      const usageCount = usage?.length || 0
      
      // Check usage limit
      if (offer.usage_limit_per_customer && usageCount >= offer.usage_limit_per_customer) {
        console.log(`ℹ️ Offer ${offer.offer_code} already used by customer`)
        continue
      }
      
      // Check offer type eligibility
      if (offer.offer_type === 'first_order' && customer.total_orders > 0) {
        console.log(`ℹ️ Offer ${offer.offer_code} is for first orders only`)
        continue
      }
      
      applicableOffers.push(offer)
    }
    
    console.log(`✅ Found ${applicableOffers.length} applicable offers`)
    return applicableOffers
  } catch (error) {
    console.error('❌ Error getting applicable offers:', error)
    return []
  }
}

/**
 * Create order with device tracking
 * @param {Object} orderData - Order data
 * @param {Object} customerInfo - Customer information
 * @returns {Promise<Object>} - { order, customer, applicableOffers }
 */
export const createOrderWithTracking = async (orderData, customerInfo) => {
  try {
    console.log('📦 Creating order with device tracking...', { orderData, customerInfo })
    
    // Get device ID
    const deviceId = getDeviceId()
    
    if (!deviceId) {
      console.warn('⚠️ No device ID found - proceeding without device tracking')
    }
    
    // Get session ID
    const sessionId = sessionStorage.getItem('ordyrr_session_id')
    
    // Validate phone number
    const phoneValidation = validatePhoneNumber(customerInfo.phone)
    
    if (!phoneValidation.isValid) {
      throw new Error(phoneValidation.reason)
    }
    
    if (phoneValidation.isSuspicious) {
      console.warn('⚠️ Suspicious phone number detected:', phoneValidation.reason)
      // Still allow order to proceed, but log the warning
    }
    
    // Find or create customer
    const customer = await findOrCreateCustomer(
      customerInfo.name,
      customerInfo.phone,
      customerInfo.email,
      deviceId
    )
    
    // Track phone usage
    if (deviceId && customerInfo.phone) {
      await trackPhoneUsage(customerInfo.phone, deviceId, customer.id)
    }
    
    // Get applicable offers
    const applicableOffers = await getApplicableOffers(customer)
    
    // Add device fingerprint to order data
    const enhancedOrderData = {
      ...orderData,
      device_fingerprint: deviceId,
      customer_id: customer.id
    }
    
    // Update customer session with customer info
    if (sessionId) {
      const { error: sessionUpdateError } = await supabase
        .from('customer_sessions')
        .update({
          customer_id: customer.id,
          customer_name: customer.full_name,
          customer_phone: customer.phone,
          customer_email: customer.email,
          last_activity_at: new Date().toISOString()
        })
        .eq('id', sessionId)
      
      if (sessionUpdateError) {
        console.warn('⚠️ Could not update customer session:', sessionUpdateError)
      }
    }
    
    console.log('✅ Order data prepared with device tracking')
    
    return {
      orderData: enhancedOrderData,
      customer,
      applicableOffers,
      phoneValidation
    }
  } catch (error) {
    console.error('❌ Error creating order with tracking:', error)
    throw error
  }
}

/**
 * Update device activity timestamp
 * @param {string} deviceId - Device fingerprint
 */
export const updateDeviceActivity = async (deviceId) => {
  try {
    if (!deviceId) return
    
    const { error } = await supabase
      .from('device_tracking')
      .update({
        last_seen_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('device_fingerprint', deviceId)
    
    if (error) {
      console.warn('⚠️ Could not update device activity:', error)
    }
  } catch (error) {
    console.warn('⚠️ Error updating device activity:', error)
  }
}

export default {
  initializeDeviceSession,
  findOrCreateCustomer,
  trackPhoneUsage,
  getApplicableOffers,
  createOrderWithTracking,
  updateDeviceActivity
}
